import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * Phase 10 — Semantic Internal Linking Grid
 *
 * Renders 30+ contextually relevant links grouped by category. Each link
 * includes a short blurb for semantic clarity (not just anchor text).
 *
 * Props:
 *   - groups: [{ title, links: [{ name, url, blurb }] }]
 *
 * Guarantees:
 *   - Total link count >= 30 (Phase 10 target)
 *   - Each link has descriptive anchor text + blurb (semantic, not keyword-stuffed)
 *   - Grouped for scannability + topical clustering
 */
const SemanticLinkGrid = ({ groups = [], title = 'Explore more' }) => {
  if (!groups.length) return null;
  const total = groups.reduce((n, g) => n + (g.links?.length || 0), 0);

  return (
    <section className="py-10 border-t border-slate-100" data-testid="semantic-link-grid">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-1">{title}</h2>
        <p className="text-slate-500 text-sm mb-6" data-testid="semantic-link-count">
          {total}+ curated resources across {groups.length} topics
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {groups.map((g, gi) => (
            <div key={gi} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`semantic-group-${gi}`}>
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">{g.title}</h3>
              <ul className="space-y-2">
                {(g.links || []).map((l, li) => (
                  <li key={li}>
                    <Link
                      to={l.url}
                      className="group flex items-start gap-2 text-sm text-slate-700 hover:text-pink-600 transition-colors"
                      data-testid={`semantic-link-${gi}-${li}`}
                    >
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-pink-500 mt-0.5 flex-shrink-0 transition-colors" />
                      <span>
                        <span className="font-medium underline-offset-2 group-hover:underline">{l.name}</span>
                        {l.blurb && <span className="block text-xs text-slate-500 mt-0.5">{l.blurb}</span>}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Curated semantic link groups — reusable across page templates
// ============================================================================
export const MARKETPLACE_SEMANTIC_LINKS = [
  {
    title: 'Marketplace Consulting',
    links: [
      { name: 'Myntra Consultant', url: '/myntra-account-management', blurb: '10–15 day approvals + full account ops' },
      { name: 'Nykaa Consultant', url: '/nykaa-account-management', blurb: 'Beauty & personal-care specialists' },
      { name: 'AJIO Consultant', url: '/ajio-account-management', blurb: 'AJIO Indie + AJIO Luxe onboarding' },
      { name: 'Tata CLiQ Consultant', url: '/marketplaces', blurb: 'Fashion + electronics + CLiQ Luxury' },
      { name: 'Blinkit Consultant', url: '/blinkit-account-management', blurb: 'Quick-commerce onboarding + dark-store ops' },
      { name: 'Zepto Consultant', url: '/zepto-account-management', blurb: '10-min delivery + city-level ops' },
      { name: 'Swiggy Instamart', url: '/swiggy-instamart-account-management', blurb: 'FMCG + personal-care quick-commerce' },
    ],
  },
  {
    title: 'Deep-Topic Guides',
    links: [
      { name: 'Myntra Commission Structure', url: '/myntra-commission', blurb: '18–32% commission breakdown + fees' },
      { name: 'Nykaa Brand Approval', url: '/nykaa-documents-required', blurb: 'Complete document checklist' },
      { name: 'Blinkit API Integration', url: '/blinkit-api-integration', blurb: 'Order + inventory + settlement APIs' },
      { name: 'Zepto Automation', url: '/zepto-automation', blurb: 'AI-driven automation stack' },
      { name: 'Myntra Returns Reduction', url: '/myntra-returns', blurb: '25–40% return rate reduction playbook' },
      { name: 'AJIO Advertising Guide', url: '/ajio-advertising', blurb: 'PLA + Sponsored Brand ROAS optimization' },
      { name: 'Tata CLiQ Documents', url: '/tata-cliq-documents-required', blurb: 'Brand-authorization pack requirements' },
    ],
  },
  {
    title: 'Comparisons & Alternatives',
    links: [
      { name: 'Myntra vs AJIO', url: '/myntra-vs-ajio', blurb: 'Head-to-head fashion marketplace comparison' },
      { name: 'Nykaa vs Tata CLiQ', url: '/nykaa-vs-tata-cliq', blurb: 'Beauty & premium fashion showdown' },
      { name: 'Blinkit vs Zepto', url: '/blinkit-vs-zepto', blurb: 'Quick-commerce head-to-head' },
      { name: 'Myntra vs Nykaa Fashion', url: '/myntra-vs-nykaa-fashion', blurb: 'Fashion vs premium curated' },
      { name: 'Best Myntra Alternatives', url: '/best-alternatives-to-myntra', blurb: 'Top 6 alternative marketplaces ranked' },
      { name: 'Best Blinkit Alternatives', url: '/best-alternatives-to-blinkit', blurb: 'Quick-commerce alternatives ranked' },
    ],
  },
  {
    title: 'Zaroori Jini OMS',
    links: [
      { name: 'Zaroori Jini OMS Hub', url: '/zaroori-jini-oms', blurb: "India's founder-led AI-first OMS" },
      { name: 'OMS Pricing', url: '/zaroori-jini-oms/pricing', blurb: '3 transparent tiers · from ₹4,999/month' },
      { name: 'ROI Calculator', url: '/zaroori-jini-oms/roi-calculator', blurb: 'Estimate annual savings live' },
      { name: 'Best OMS Software India', url: '/zaroori-jini-oms/best-oms-software-india', blurb: '8 platforms compared for 2026' },
      { name: 'vs Unicommerce', url: '/zaroori-jini-oms-vs-unicommerce', blurb: '14-row side-by-side comparison' },
      { name: 'AI Order Management', url: '/zaroori-jini-oms/ai-order-management', blurb: 'AI repricing + bid manager + forecasting' },
    ],
  },
  {
    title: 'Industry Consulting',
    links: [
      { name: 'Myntra Fashion Consultant', url: '/myntra-fashion-consultant', blurb: 'Fashion category deep-expertise' },
      { name: 'Nykaa Beauty Consultant', url: '/nykaa-fashion-beauty-consultant', blurb: 'Beauty + personal-care specialists' },
      { name: 'Blinkit FMCG Consultant', url: '/blinkit-fmcg-consultant', blurb: 'Quick-commerce FMCG onboarding' },
      { name: 'Amazon Fashion Consultant', url: '/amazon-fashion-consultant', blurb: 'Amazon Fashion + Luxury Stores' },
    ],
  },
  {
    title: 'Answers & Guides',
    links: [
      { name: 'How to sell on Myntra', url: '/answers/how-to-sell-on-myntra', blurb: 'Complete 30-day launch playbook' },
      { name: 'Myntra brand approval process', url: '/blog/myntra-brand-approval-process-2026', blurb: 'Fact-checked 2026 process' },
      { name: 'Nykaa brand approval', url: '/blog/nykaa-brand-approval-process-2026', blurb: 'Beauty-specific approval flow' },
      { name: 'AJIO seller registration', url: '/blog/ajio-seller-registration-guide-2026', blurb: 'AJIO Indie + Luxe walkthrough' },
      { name: 'Deepinder Ahluwalia — Founder', url: '/deepinder-ahluwalia', blurb: 'Meet the founder, credentials & press' },
      { name: 'Contact Zaroori Retail', url: '/contact', blurb: 'Free strategy call with founder' },
    ],
  },
];

export default SemanticLinkGrid;
